import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { siteBillingService } from "../../api/services/site-billing.service";
import type { SiteBillingMeStatus } from "../../api/types/site-billing";

const POS_LOGIN_URL = "https://pos.amzinfra.site/login";
const INFRA_ADMIN_BILLING_URL = "https://www.amzinfra.site/admin/billing";
const INFRA_ADMIN_SITES_URL = "https://www.amzinfra.site/admin/sites";
const INFRA_SITE_BILLING_URL = "https://www.amzinfra.site/site/billing";

type BillingStatusCardProps = {
  hasSiteOwner: boolean;
};

const money = (value?: number | null) => `${Number(value ?? 0).toLocaleString()}원`;

export function BillingManagementCard() {
  return (
    <div className="card card-height-100">
      <div className="card-body">
        <h4 className="card-title mb-3">사용료 관리</h4>
        <div className="d-flex flex-wrap gap-2">
          <ExternalButton href={INFRA_ADMIN_BILLING_URL}>청구서 관리</ExternalButton>
          <ExternalButton href={INFRA_ADMIN_SITES_URL}>지점 관리</ExternalButton>
          <ExternalButton href={POS_LOGIN_URL}>Amazing POS</ExternalButton>
        </div>
      </div>
    </div>
  );
}

export default function BillingStatusCard({ hasSiteOwner }: BillingStatusCardProps) {
  const [billingStatus, setBillingStatus] = useState<SiteBillingMeStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!hasSiteOwner) return;
    let isMounted = true;
    setIsLoading(true);
    siteBillingService
      .getMyStatus()
      .then((response) => {
        if (isMounted) setBillingStatus(response.data);
      })
      .catch((error) => {
        console.warn("Failed to load site billing status", error);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [hasSiteOwner]);

  const statusVisible = Boolean(billingStatus?.enabled);

  return (
    <div className="card card-height-100">
      <div className="card-body">
        <h4 className="card-title mb-3">사용료 현황</h4>
        {isLoading ? (
          <div className="d-flex align-items-center gap-2 text-muted">
            <span className="spinner-border spinner-border-sm" role="status" />
            <span>로딩 중</span>
          </div>
        ) : statusVisible ? (
          <div className="d-grid gap-2">
            <div className="fw-semibold text-dark">
              청구서 {billingStatus?.latest_invoice_number || "-"}
            </div>
            <div className="d-flex flex-wrap gap-2">
              <span className="badge bg-warning-subtle text-warning">미납 {money(billingStatus?.unpaid_balance)}</span>
              <span className="badge bg-danger-subtle text-danger">연체 {money(billingStatus?.overdue_balance)}</span>
              <span className="badge bg-light text-muted">납기 {billingStatus?.latest_due_date || "-"}</span>
            </div>
            <div className="text-muted small">
              청구액 {money(billingStatus?.latest_amount_due)} / 잔액 {money(billingStatus?.latest_balance_due)}
            </div>
          </div>
        ) : (
          <div className="text-muted">표시할 사용료 청구서가 없습니다.</div>
        )}
        <div className="mt-3 d-flex flex-wrap gap-2">
          <InternalButton to="/statistics-adjustment">정산내역 보기</InternalButton>
          <ExternalButton href={billingStatus?.infra_billing_url || INFRA_SITE_BILLING_URL}>Amazing Infra</ExternalButton>
          <ExternalButton href={POS_LOGIN_URL}>Amazing POS</ExternalButton>
        </div>
      </div>
    </div>
  );
}

function InternalButton({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link className="btn btn-sm btn-primary" to={to}>
      {children}
    </Link>
  );
}

function ExternalButton({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a className="btn btn-sm btn-soft-primary" href={href} target="_blank" rel="noreferrer">
      {children}
      <i className="ri-external-link-line ms-1" />
    </a>
  );
}
